// Carriers API client — Phase 4 Plan 04-04 Task 2 (FWD-04, FWD-05).
//
// Wraps:
//   GET    /api/v1/carriers                       (list + search + pagination)
//   POST   /api/v1/carriers                       (create — Idempotency-Key)
//   PATCH  /api/v1/carriers/{id}                  (update)
//   DELETE /api/v1/carriers/{id}                  (hard delete)
//   POST   /api/v1/carriers/{id}/assign-trip      (assign — Idempotency-Key)

import type { ApiClient } from "./client";

export interface CarrierResponse {
  id: number;
  carrier_name: string;
  contact_email: string | null;
  contact_telegram: string | null;
  contact_phone: string | null;
  rating: number | null;
  notes: string | null;
  linked_company_id: number | null;
  created_at: string;
}

export interface CarrierListResponse {
  items: CarrierResponse[];
  total: number;
  offset: number;
  limit: number;
}

export interface CarrierCreateBody {
  carrier_name: string;
  contact_email?: string | null;
  contact_telegram?: string | null;
  contact_phone?: string | null;
  rating?: number | null;
  notes?: string | null;
}

export interface CarrierUpdateBody extends Partial<CarrierCreateBody> {}

export interface AssignmentResponse {
  immediate: boolean;
  assignment_id: number | null;
  invite_deep_link: string | null;
  carrier_name: string;
  trip_id: number;
}

function generateIdempotencyKey(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export const carriersApi = {
  list(
    client: ApiClient,
    opts: { search?: string; offset?: number; limit?: number } = {},
  ): Promise<CarrierListResponse> {
    const params = new URLSearchParams();
    if (opts.search) params.set("search", opts.search);
    if (opts.offset !== undefined) params.set("offset", String(opts.offset));
    if (opts.limit !== undefined) params.set("limit", String(opts.limit));
    const qs = params.toString();
    return client.get<CarrierListResponse>(`/api/v1/carriers${qs ? `?${qs}` : ""}`);
  },

  create(client: ApiClient, body: CarrierCreateBody): Promise<CarrierResponse> {
    return client.fetch<CarrierResponse>("/api/v1/carriers", {
      method: "POST",
      headers: { "Idempotency-Key": generateIdempotencyKey() },
      body: JSON.stringify(body),
    });
  },

  update(client: ApiClient, carrierId: number, body: CarrierUpdateBody): Promise<CarrierResponse> {
    return client.fetch<CarrierResponse>(`/api/v1/carriers/${carrierId}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    });
  },

  delete(client: ApiClient, carrierId: number): Promise<void> {
    return client.fetch<void>(`/api/v1/carriers/${carrierId}`, {
      method: "DELETE",
    });
  },

  assignTrip(client: ApiClient, carrierId: number, tripId: number): Promise<AssignmentResponse> {
    return client.fetch<AssignmentResponse>(`/api/v1/carriers/${carrierId}/assign-trip`, {
      method: "POST",
      headers: { "Idempotency-Key": generateIdempotencyKey() },
      body: JSON.stringify({ trip_id: tripId }),
    });
  },
};
